/** Screen-reader announcement copy for a failed listing fetch — shared with
 * the visible error state (`ProductGridErrorState`) so both surfaces say the
 * same thing. */
export const PLP_ERROR_MESSAGE = "We couldn't load the products.";

/** Mirrors `formatPaginationRangeLabel` from `@chemist4u/newt-components`
 * (the copy `ResultsCount` renders in the toolbar and pager) so the
 * announcement matches what sighted users see. Kept local instead of
 * imported from the DS barrel so browser tests that stub the whole package
 * (`vi.mock('@chemist4u/newt-components')`) still exercise the real
 * announcement copy. */
function formatRangeLabel(
  page: number,
  pageSize: number,
  totalItems: number,
): string {
  if (totalItems <= 0 || pageSize <= 0) return '0 of 0 products';
  const totalPages = Math.ceil(totalItems / pageSize);
  if (totalPages <= 1) return `${totalItems} out of ${totalItems} products`;
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, totalItems);
  return `${from} - ${to} of ${totalItems} products`;
}

type PlpLiveStatusProps = {
  isLoading: boolean;
  hasError?: boolean;
  page: number;
  pageSize: number;
  totalItems: number;
};

/** Persistent polite live region announcing PLP result changes to screen
 * readers. Must stay mounted across loading/loaded/error states — a live
 * region inserted into the DOM *together with* its content (like the old
 * spinner-only `aria-live` wrapper) is announced inconsistently across
 * screen readers; only content changes inside an existing region are
 * reliable. The initial SSR content is intentionally not announced. */
export default function PlpLiveStatus({
  isLoading,
  hasError = false,
  page,
  pageSize,
  totalItems,
}: PlpLiveStatusProps) {
  let message = `Showing ${formatRangeLabel(page, pageSize, totalItems)}`;
  if (hasError) {
    message = PLP_ERROR_MESSAGE;
  } else if (isLoading) {
    message = 'Loading products…';
  }

  return (
    <p role="status" aria-live="polite" className="sr-only">
      {message}
    </p>
  );
}
